import {
  CustomMappingType,
  IFailedCustomMappingRecord,
  ISourceEntityTypes,
  type ICustomMapping,
  type ICustomMappingSourceOption,
  type IPracticeMigration,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  FeeSchedule,
  toINamedDocuments,
} from '@principle-theorem/principle-core';
import { type IFeeSchedule } from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  IBlobFilenamePair,
  XSLXImporterExporter,
  asDocRef,
  multiMap,
  multiSortBy$,
  nameSorter,
  reduce2DArray,
  snapshot,
  toNamedDocument,
  type INamedDocument,
  type WithRef,
} from '@principle-theorem/shared';
import { compact, sortBy } from 'lodash';
import { combineLatest } from 'rxjs';
import { BaseCustomMappingHandler } from '../base-custom-mapping-handler';
import { CustomMapping } from '../custom-mapping';
import { PracticeMigration } from '../practice-migrations';
import { BaseSourceEntity } from '../source/base-source-entity';
import { TranslationMapHandler } from '../translation-map';
import { FeeSchedulesToXSLX } from './fee-schedule-to-xlsx';
import { XSLXToFeeSchedule } from './xlsx-to-fee-schedule';

export interface IBaseMigrationFeeSchedule {
  id: string | number;
  name: string;
}

export const FEE_SCHEDULE_RESOURCE_TYPE = 'feeSchedules';

export const FEE_SCHEDULES_MAPPING: ICustomMapping = CustomMapping.init({
  metadata: {
    label: 'Fee Schedules',
    description:
      'Here you can override the fee schedule mapping in cases where fee schedules have already been created in Principle. You can also map old outdated fee schedules to a fee schedule in Principle. Leaving a fee schedule blank will create it in Principle.',
    type: FEE_SCHEDULE_RESOURCE_TYPE,
  },
  type: CustomMappingType.DocumentReference,
  labelOverrides: {
    sourceIdentifier: 'Id',
    sourceLabel: 'Fee Schedule',
    destinationIdentifier: 'Principle Fee Schedule',
  },
});

export abstract class FeeScheduleMappingHandler<
  FeeSchedule extends ISourceEntityTypes,
  SourceEntity extends BaseSourceEntity<FeeSchedule>,
> extends BaseCustomMappingHandler<IFeeSchedule> {
  customMapping = FEE_SCHEDULES_MAPPING;
  abstract sourceEntity: SourceEntity;
  abstract translateFn: (
    record: FeeSchedule['data']
  ) => IBaseMigrationFeeSchedule;

  async getSourceOptions(
    migration: WithRef<IPracticeMigration>
  ): Promise<ICustomMappingSourceOption[]> {
    const records = await this.sourceEntity
      .getAllRecords$(migration)
      .toPromise();
    const options = records
      .map((record) => record.data)
      .map((record) => {
        const feeSchedule = this.translateFn(record);
        return {
          label: `${feeSchedule.name}`,
          value: this.sourceEntity.getSourceRecordId(record).toString(),
        };
      });

    return sortBy(options, 'label');
  }

  async getDestinationOptions(
    migration: WithRef<IPracticeMigration>
  ): Promise<INamedDocument<IFeeSchedule>[]> {
    return snapshot(
      combineLatest([
        FeeSchedule.all$(migration.configuration.organisation).pipe(
          multiMap((feeSchedule) => ({
            ...feeSchedule,
            name: `Workspace - ${feeSchedule.name}`,
          })),
          toINamedDocuments()
        ),
        ...migration.configuration.practices.map((practice) =>
          FeeSchedule.all$(practice).pipe(
            multiMap((feeSchedule) => ({
              ...feeSchedule,
              name: `${practice.name} - ${feeSchedule.name}`,
            })),
            toINamedDocuments()
          )
        ),
      ]).pipe(reduce2DArray(), multiSortBy$(nameSorter()))
    );
  }

  async getMappingBlob(
    migration: WithRef<IPracticeMigration>
  ): Promise<IBlobFilenamePair> {
    const { fileName, feeSchedules, translator } =
      await this._getExporterData(migration);

    return new XSLXImporterExporter().getBlob(
      fileName,
      feeSchedules,
      translator
    );
  }

  async downloadMapping(migration: WithRef<IPracticeMigration>): Promise<void> {
    const { fileName, feeSchedules, translator } =
      await this._getExporterData(migration);

    await new XSLXImporterExporter().download(
      fileName,
      feeSchedules,
      translator
    );
  }

  async uploadMapping(
    migration: WithRef<IPracticeMigration>,
    file: File
  ): Promise<void> {
    const items = await new XSLXImporterExporter().parse(
      file,
      new XSLXToFeeSchedule()
    );

    const translationMap = new TranslationMapHandler(migration);
    const sourceOptions = await this.getSourceOptions(migration);
    const feeSchedules = await this.getDestinationOptions(migration);

    const failedRecords: IFailedCustomMappingRecord[] = [];

    const records = items.map((item) => {
      const mapTo = item.mapTo;
      const matchingOption = sourceOptions.find(
        (option) => option.label === item.label
      );
      if (!matchingOption || !mapTo) {
        return;
      }

      const feeSchedule = feeSchedules.find(
        (schedule) => schedule.name === mapTo
      );
      if (!feeSchedule) {
        // eslint-disable-next-line no-console
        console.error(
          `Mapping error: ${this.customMapping.metadata.label} - Couldn't find fee schedule for item`,
          item
        );

        failedRecords.push({
          sourceIdentifier: matchingOption.value,
          sourceLabel: matchingOption.label,
          reason: `Destination identifier "${mapTo?.trim()}" not found`,
        });
        return;
      }

      return {
        destinationIdentifier: feeSchedule.ref,
        destinationValue: { value: item.mapTo },
        sourceIdentifier: matchingOption.value,
        sourceLabel: matchingOption.label,
      };
    });

    await this.upsertBulk(compact(records), translationMap);

    const customMapping = await snapshot(
      PracticeMigration.findCustomMapping$(migration, this.customMapping)
    );
    if (!customMapping) {
      throw new Error('Custom mapping not found for updating failed records');
    }
    await Firestore.patchDoc(customMapping.ref, {
      failedRecords,
    });
  }

  private async _getExporterData(
    migration: WithRef<IPracticeMigration>
  ): Promise<{
    fileName: string;
    feeSchedules: IBaseMigrationFeeSchedule[];
    translator: FeeSchedulesToXSLX;
  }> {
    const fileName = this.getFileName();
    const feeSchedules = await this._getSourceSchedules(migration);
    const translationMap = new TranslationMapHandler(migration);
    const translator = new FeeSchedulesToXSLX(
      await this.getDestinationOptions(migration),
      await this.getRecords(translationMap)
    );
    return { fileName, feeSchedules, translator };
  }

  private async _getSourceSchedules(
    migration: WithRef<IPracticeMigration>
  ): Promise<IBaseMigrationFeeSchedule[]> {
    const records = await this.sourceEntity
      .getAllRecords$(migration)
      .toPromise();
    return sortBy(
      records.map((record) => this.translateFn(record.data)),
      'name'
    );
  }
}

export type FeeScheduleResolverFn = (
  id?: string
) => Promise<INamedDocument<IFeeSchedule>>;

export function resolveFeeSchedule(
  translationMap: TranslationMapHandler,
  defaultFeeSchedule: INamedDocument<IFeeSchedule>
): FeeScheduleResolverFn {
  const namedDefaultFeeSchedule = toNamedDocument(defaultFeeSchedule);

  return async (id?: string) => {
    if (!id) {
      return namedDefaultFeeSchedule;
    }
    const resolved = await translationMap.getBySource(
      id,
      FEE_SCHEDULE_RESOURCE_TYPE
    );
    if (!resolved || !resolved.destinationIdentifier) {
      return namedDefaultFeeSchedule;
    }
    return toNamedDocument(
      await Firestore.getDoc(
        asDocRef<IFeeSchedule>(resolved.destinationIdentifier)
      )
    );
  };
}
